import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as itinerariesApi from './itineraries.api';

import type {
  VoidApprovalApproveRequest,
  VoidApprovalCreateRequest,
  VoidApprovalListItem,
  VoidApprovalListQuery,
  VoidApprovalRejectRequest,
} from '@utazzo/shared';

import { extractServerError } from '@/lib/api-error';

/**
 * React Query hooks for the void-a-won-deal approval queue. Mirrors
 * `use-reprice-approvals.ts`: the list is reviewer-scoped server-side (ADMIN
 * all, SALES_LEAD assigned, SALES_AGENT their own), so the query key carries
 * the full request query.
 */

const extractMessage = (err: unknown): string | null => extractServerError(err).message;

export const VOID_APPROVALS_QK = {
  all: () => ['itineraries', 'void-approvals'] as const,
  list: (query: VoidApprovalListQuery) => ['itineraries', 'void-approvals', query] as const,
  pendingCount: () => ['itineraries', 'void-approvals', 'pending-count'] as const,
};

export const useVoidApprovals = (query: VoidApprovalListQuery) =>
  useQuery({
    queryKey: VOID_APPROVALS_QK.list(query),
    queryFn: () => itinerariesApi.listVoidApprovals(query),
    staleTime: 15_000,
  });

export const useVoidApprovalsPendingCount = (enabled = true) =>
  useQuery({
    queryKey: VOID_APPROVALS_QK.pendingCount(),
    queryFn: () => itinerariesApi.getVoidApprovalsPendingCount(),
    enabled,
    refetchInterval: 60_000,
    retry: false,
    throwOnError: false,
  });

/** Raise a void request against an accepted itinerary of a won deal. */
export const useRequestVoid = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: VoidApprovalCreateRequest): Promise<VoidApprovalListItem> =>
      itinerariesApi.requestVoidApproval(payload),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: VOID_APPROVALS_QK.all() });
      void qc.invalidateQueries({ queryKey: ['itineraries', 'detail', row.itineraryId] });
      toast.success(
        row.status === 'APPROVED' ? 'Deal voided' : 'Void request submitted for approval',
      );
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to submit void request'),
  });
};

/** Approve a pending void request - runs the void executor server-side. */
export const useApproveVoid = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      remark,
    }: {
      id: string;
      remark?: VoidApprovalApproveRequest['remark'];
    }): Promise<VoidApprovalListItem> => itinerariesApi.approveVoidApproval(id, { remark }),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: VOID_APPROVALS_QK.all() });
      void qc.invalidateQueries({ queryKey: ['itineraries', 'detail', row.itineraryId] });
      toast.success('Void approved');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to approve void'),
  });
};

/** Reject a pending void request. A remark is required. */
export const useRejectVoid = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      remark,
    }: {
      id: string;
      remark: VoidApprovalRejectRequest['remark'];
    }): Promise<VoidApprovalListItem> => itinerariesApi.rejectVoidApproval(id, { remark }),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: VOID_APPROVALS_QK.all() });
      void qc.invalidateQueries({ queryKey: ['itineraries', 'detail', row.itineraryId] });
      toast.success('Void request rejected');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to reject void'),
  });
};
